// =============================================================================
// Language-aware date formatting.
//
// Every user-visible date in the app goes through here. Both halves of the
// problem live in one place: the *locale* (month and weekday names) and the
// *pattern* (German writes "24. August 2026", English "24 August 2026" — the
// ordinal dot is not just decoration, it is wrong in English).
//
// Callers ask for a semantic shape (`long`, `short`, `monthYear`, …) rather
// than a format string, so a pattern only ever has to be fixed once.
// =============================================================================

import { format, parseISO, isValid } from 'date-fns';
import { getLang, localeFor, useTranslation } from './i18n';

// Patterns per shape. `de` is the app's original wording; `en` drops the
// ordinal dot after the day number and keeps everything else identical.
const PATTERNS = {
  //                              de                        en
  full: ['EEEE, d. MMMM yyyy', 'EEEE, d MMMM yyyy'],
  long: ['d. MMMM yyyy', 'd MMMM yyyy'],
  dayMonthLong: ['d. MMMM', 'd MMMM'],
  medium: ['d. MMM yyyy', 'd MMM yyyy'],
  short: ['d. MMM', 'd MMM'],
  weekdayFull: ['EEE, d. MMMM yyyy', 'EEE, d MMMM yyyy'],
  weekdayShort: ['EEE, d. MMM', 'EEE, d MMM'],
  weekdayLong: ['EEEE, d. MMM', 'EEEE, d MMM'],
  shortTime: ['d. MMM, HH:mm', 'd MMM, HH:mm'],
  weekdayShortTime: ['EEE d. MMM, HH:mm', 'EEE d MMM, HH:mm'],
  dayNum: ['d.', 'd'],
  monthYear: ['MMMM yyyy', 'MMMM yyyy'],
  month: ['MMMM', 'MMMM'],
  time: ['HH:mm', 'HH:mm']
};

export const patternFor = (shape, lang = getLang()) => {
  const pair = PATTERNS[shape];
  if (!pair) return shape; // allow a raw date-fns pattern as an escape hatch
  return lang === 'en' ? pair[1] : pair[0];
};

const toDate = (value) => {
  if (value instanceof Date) return value;
  if (typeof value === 'string') return parseISO(value);
  if (typeof value === 'number') return new Date(value);
  return null;
};

// Format `value` (Date, ISO string or timestamp) in the active language.
// Returns '' for anything unparseable so a bad row can never crash a render.
export const formatDate = (value, shape = 'medium', lang = getLang()) => {
  const date = toDate(value);
  if (!date || !isValid(date)) return '';
  return format(date, patternFor(shape, lang), { locale: localeFor(lang) });
};

// Hook form. Prefer this inside components: it subscribes to the language
// context, so switching language re-renders the dates. The bare `formatDate`
// reads the stored language and is for module-level helpers and non-React code.
export const useFormatDate = () => {
  const { lang } = useTranslation();
  return (value, shape = 'medium') => formatDate(value, shape, lang);
};

export { getLang, localeFor };
